import { adoptSession, type LoginResponse } from '@/features/auth';
import { http, type ApiError } from '@/shared/api';
import { useMutation, useQuery } from '@tanstack/react-query';
import type { UUID } from '@/shared/types/api';

import { demoSession } from '../lib/demo-session';

export interface DemoStatus {
  enabled: boolean;
  generated_at: string;
}

/** The demo login response: a normal login plus where to go next. */
export interface DemoLoginResponse extends LoginResponse {
  demo: true;
  building_id: UUID | null;
  /** The outstanding charge provisioned for this walkthrough. */
  unit_charge_id: UUID | null;
}

export const demoApi = {
  /**
   * Whether the demo door is open. Server-owned so there is a single source of
   * truth — a client-side flag would drift from the backend's `DEMO_MODE`.
   */
  getStatus: () => http.get<DemoStatus>('/auth/demo/status'),

  login: () => http.post<DemoLoginResponse>('/auth/demo'),
} as const;

/**
 * Polled once on the login screen. Failures are silent: if the check cannot be
 * made, the demo button simply stays hidden.
 */
export function useDemoStatus() {
  return useQuery<DemoStatus, ApiError>({
    queryKey: ['demo', 'status'],
    queryFn: demoApi.getStatus,
    staleTime: Infinity,
    retry: false,
  });
}

export function useDemoLogin() {
  return useMutation<DemoLoginResponse, ApiError, void>({
    mutationFn: demoApi.login,
    onSuccess: (response) => {
      adoptSession(response);
      // `adoptSession` clears the query cache, so the demo markers are written
      // afterwards — they live in sessionStorage and are unaffected either way.
      demoSession.start({
        unitChargeId: response.unit_charge_id,
        buildingId: response.building_id,
      });
    },
  });
}
